/**
 * Lógica do contexto clínico do Preceptor AI.
 *
 * O contexto que o aluno escreve não é "opcional que melhora" — é determinante.
 * O mesmo caso (metástases císticas), com as mesmas imagens, recebeu "cisto
 * hidático" sem contexto e "metástases encefálicas" com uma linha de história.
 * Este módulo é o que sustenta o campo que orienta esse preenchimento.
 *
 * Fica em `utils/` (e não dentro do componente) porque é aqui que moram os casos
 * de borda, porque é idêntico entre posexpert e neuronews, e porque assim dá
 * para testar tudo sem montar DOM.
 */

import i18n from '../config/i18n';

export type Sexo = 'M' | 'F';

export interface CamposPaciente {
  idade: string;
  sexo: Sexo | null;
}

export interface PrefixoExtraido extends CamposPaciente {
  /** O texto sem o prefixo de identificação. */
  restante: string;
}

export interface Insercao {
  texto: string;
  caret: number;
}

export type TipoRotulo = 'history' | 'sequences' | 'question';
export type Criterio = 'identificacao' | 'historia' | 'duvida';
export type NivelContexto = 'absent' | 'weak' | 'good' | 'complete';

export interface Pontuacao {
  identificacao: boolean;
  historia: boolean;
  duvida: boolean;
  pontos: 0 | 1 | 2 | 3;
  nivel: NivelContexto;
  /** Primeiro critério não atendido, na ordem id → história → dúvida. */
  faltando: Criterio | null;
}

/** Caracteres mínimos no texto livre para valer o ponto de história clínica. */
export const MIN_CHARS_HISTORIA = 40;

/** Teto do textarea. Deixa folga para o prefixo dentro do limite do DTO. */
export const MAX_CHARS_TEXTO = 8000;

const NIVEIS: readonly NivelContexto[] = ['absent', 'weak', 'good', 'complete'];

/**
 * Idade escrita dentro do texto livre.
 *
 * `a[nñ]os?` cobre ano/anos/año/años — o singular importa em neuropediatria
 * ("1 ano"). O lookahead descarta intervalos, que são frequentes em laudo:
 * "3 a 5 lesões" e "de 2 a 3 cm" não são idade.
 */
const RE_IDADE = /\b\d{1,3}\s*(?:a[nñ]os?|a)\b(?!\s*\d)/i;

/** Interrogação nos dois idiomas — o `¿` do espanhol abre, o `?` fecha. */
const RE_INTERROGACAO = /[?¿]/;

/**
 * Minúsculas, sem diacríticos, espaços colapsados.
 *
 * A decomposição NFD separa o `ñ` de "años" em `n` + til combinante, então o
 * til cai junto com os outros acentos e "Sequências"/"Secuencias" passam a
 * casar entre si. É o que faz o reconhecimento de rótulo funcionar quando o
 * aluno cola um texto no idioma que não é o da interface.
 */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Idade digitada: só dígitos, no máximo 3.
 *
 * `'0'` é idade válida (recém-nascido) e pontua. `'999'` passa: validar faixa
 * criaria uma regra a mais para impedir o aluno de gerar uma frase boba — e
 * nada aqui bloqueia o envio, por princípio.
 */
export function sanitizarIdade(valor: string): string {
  return valor
    .replace(/\D/g, '')
    .slice(0, 3)
    .replace(/^0+(?=\d)/, '');
}

/** O rótulo já está no texto? Ignora capitalização, acento e idioma. */
export function rotuloPresente(texto: string, rotulo: string): boolean {
  return normalizar(texto).includes(normalizar(rotulo));
}

/**
 * Insere o rótulo de um chip no textarea.
 *
 * Três regras, nesta ordem:
 *
 * 1. O ponto de inserção é o **fim** da seleção, nunca o início: com texto
 *    selecionado o rótulo entra depois dele, sem apagar nada. Substituir a
 *    seleção destruiria texto do aluno com um clique — e num campo controlado
 *    o desfazer do navegador já não funciona.
 * 2. Caret estritamente dentro de uma palavra salta para o fim da linha. Serrar
 *    uma palavra ao meio nunca é o que o aluno quis.
 * 3. O rótulo fica na sua própria linha, sem duplicar quebras já existentes.
 *
 * O caret volta logo após o rótulo e o espaço — pronto para escrever.
 */
export function inserirRotuloNoTexto(
  texto: string,
  rotulo: string,
  selecaoInicio: number,
  selecaoFim: number,
): Insercao {
  const limite = texto.length;
  const inicio = Math.max(0, Math.min(selecaoInicio, limite));
  let pos = Math.max(inicio, Math.min(selecaoFim, limite));

  // Regra 2: dentro de uma palavra, vai para o fim da linha.
  if (/\S/.test(texto[pos - 1] ?? '') && /\S/.test(texto[pos] ?? '')) {
    const quebra = texto.indexOf('\n', pos);
    pos = quebra === -1 ? limite : quebra;
  }

  const antes = texto.slice(0, pos);
  const depois = texto.slice(pos);
  const abre = antes === '' || antes.endsWith('\n') ? '' : '\n';
  const fecha = depois === '' || depois.startsWith('\n') ? '' : '\n';
  const miolo = `${rotulo} `;

  return {
    texto: antes + abre + miolo + fecha + depois,
    caret: (antes + abre + miolo).length,
  };
}

/**
 * Pontua a completude do contexto: 1 ponto por critério, no máximo 3.
 *
 * Nunca bloqueia o envio — é estímulo, não validação. O aluno pode ter um caso
 * legitimamente sem história (prova, autoavaliação às cegas).
 */
export function pontuarContexto(entrada: {
  texto: string;
  idade: string;
  hipotese: string;
  rotulosHistoria: string[];
  rotulosDuvida: string[];
}): Pontuacao {
  const { texto, idade, hipotese, rotulosHistoria, rotulosDuvida } = entrada;
  const normalizado = normalizar(texto);
  const contem = (rotulos: string[]) =>
    rotulos.some((r) => normalizado.includes(normalizar(r)));

  const identificacao = idade.trim() !== '' || RE_IDADE.test(texto);
  const historia =
    texto.trim().length >= MIN_CHARS_HISTORIA || contem(rotulosHistoria);
  // A hipótese vem do campo dedicado, que fica fora deste bloco: sem ela aqui,
  // quem responde no campo próprio nunca chegaria aos 3 pontos.
  const duvida =
    RE_INTERROGACAO.test(texto) ||
    contem(rotulosDuvida) ||
    hipotese.trim() !== '';

  const pontos = (Number(identificacao) +
    Number(historia) +
    Number(duvida)) as 0 | 1 | 2 | 3;

  const faltando: Criterio | null = !identificacao
    ? 'identificacao'
    : !historia
      ? 'historia'
      : !duvida
        ? 'duvida'
        : null;

  return { identificacao, historia, duvida, pontos, nivel: NIVEIS[pontos], faltando };
}

/** Junta o prefixo de identificação ao que o aluno escreveu. */
export function montarTexto(prefixo: string, corpo: string): string {
  const limpo = corpo.trimStart();
  if (!prefixo) return corpo;
  return limpo ? `${prefixo} ${limpo}` : prefixo;
}

// --- Camada ligada ao i18n -------------------------------------------------
// Importar o singleton do i18n num módulo de `utils` segue o que
// `services/preceptorService.ts` já faz para as mensagens de erro.

const idiomas = (): string[] => Object.keys(i18n.options.resources ?? {});

const traduzir = (lng: string, chave: string): string | undefined =>
  i18n.getResource(lng, 'translation', chave) as string | undefined;

/** Rótulos dos chips no idioma ativo. */
export function rotulosChips(): Record<TipoRotulo, string> {
  return {
    history: i18n.t('preceptorAI.chips.historyLabel'),
    sequences: i18n.t('preceptorAI.chips.sequencesLabel'),
    question: i18n.t('preceptorAI.chips.questionLabel'),
  };
}

/**
 * Rótulos em **todos** os idiomas, para a pontuação.
 *
 * O reconhecimento não pode depender do idioma da interface: o aluno pode colar
 * um texto que escreveu em outro lugar, e um caso salvo em português pode ser
 * reaberto numa sessão em espanhol.
 */
export function rotulosParaPontuacao(): {
  rotulosHistoria: string[];
  rotulosDuvida: string[];
} {
  const colher = (chave: string) =>
    idiomas()
      .map((lng) => traduzir(lng, chave))
      .filter((v): v is string => !!v);

  return {
    rotulosHistoria: colher('preceptorAI.chips.historyLabel'),
    // "Sequências enviadas:" conta como dúvida de propósito: é o que dá peso de
    // pontuação ao segundo chip, mesmo não sendo uma pergunta.
    rotulosDuvida: [
      ...colher('preceptorAI.chips.questionLabel'),
      ...colher('preceptorAI.chips.sequencesLabel'),
    ],
  };
}

const palavraSexo = (sexo: Sexo, lng?: string): string => {
  const chave =
    sexo === 'M' ? 'preceptorAI.quickFields.sexMale' : 'preceptorAI.quickFields.sexFemale';
  return lng ? (traduzir(lng, chave) ?? '') : i18n.t(chave);
};

/**
 * Frase de identificação que abre a pergunta enviada ao agente.
 *
 * Uma chave por combinação, nunca fragmentos concatenados: português usa "do
 * sexo" e espanhol usa "de sexo", e a preposição contraída não é fatiável. Além
 * disso, é o template inteiro que o parser abaixo precisa conseguir desfazer.
 */
export function montarPrefixoPaciente({ idade, sexo }: CamposPaciente): string {
  const n = sanitizarIdade(idade);
  const sex = sexo ? palavraSexo(sexo) : '';

  if (n && sexo) {
    return i18n.t('preceptorAI.patientPrefix.ageAndSex', { count: Number(n), sex });
  }
  if (n) {
    return i18n.t('preceptorAI.patientPrefix.ageOnly', { count: Number(n) });
  }
  if (sexo) {
    return i18n.t('preceptorAI.patientPrefix.sexOnly', { sex });
  }
  return '';
}

/**
 * Desfaz o prefixo que `montarPrefixoPaciente` gerou.
 *
 * Necessário porque `loadFromHistory` devolve ao textarea a pergunta como ela
 * foi enviada — prefixo incluso. Sem desfazer, reabrir um caso e preencher
 * idade/sexo de novo duplicaria a frase.
 *
 * O miolo é ancorado (`^…$`): "Paciente de 62 anos, tabagista, crise
 * convulsiva." **não** casa, então o texto do aluno fica intacto. Casar demais
 * aqui apagaria informação clínica, o que é bem pior do que não casar.
 */
const RE_ENVELOPE = /^Paciente ([^.]{1,60})\.\s*/i;
const RE_MIOLO =
  /^(?:de (\d{1,3}) a[nñ]os?(?:, sexo (\p{L}+))?|d[eo] sexo (\p{L}+))$/iu;

export function extrairPrefixoPaciente(texto: string): PrefixoExtraido {
  const vazio: PrefixoExtraido = { idade: '', sexo: null, restante: texto };

  const envelope = RE_ENVELOPE.exec(texto);
  if (!envelope) return vazio;

  const miolo = RE_MIOLO.exec(envelope[1]);
  if (!miolo) return vazio;

  const palavra = miolo[2] ?? miolo[3];
  let sexo: Sexo | null = null;
  if (palavra) {
    const alvo = normalizar(palavra);
    for (const lng of idiomas()) {
      if (normalizar(palavraSexo('M', lng)) === alvo) sexo = 'M';
      else if (normalizar(palavraSexo('F', lng)) === alvo) sexo = 'F';
      if (sexo) break;
    }
    // Palavra no lugar do sexo que não é sexo em idioma nenhum: não é o nosso
    // prefixo, e devolver o texto intacto é sempre o lado seguro do erro.
    if (!sexo) return vazio;
  }

  return {
    idade: miolo[1] ?? '',
    sexo,
    restante: texto.slice(envelope[0].length),
  };
}

// --- Aviso de envio sem contexto -------------------------------------------

const CHAVE_AVISO = 'preceptor:avisoContextoDispensado';

/**
 * `sessionStorage` e não `useRef` porque `PreceptorAI` é lazy e desmonta ao
 * trocar de tela: com ref, quem sai para ver uma aula e volta levaria o aviso
 * de novo. E não `localStorage` porque aí o aviso morreria para sempre.
 * Envolvido em try/catch: navegador em modo privado pode lançar na escrita.
 */
export function avisoContextoDispensado(): boolean {
  try {
    return sessionStorage.getItem(CHAVE_AVISO) === '1';
  } catch {
    return false;
  }
}

export function dispensarAvisoContexto(): void {
  try {
    sessionStorage.setItem(CHAVE_AVISO, '1');
  } catch {
    // Sem storage o aviso volta a aparecer — chato, nunca quebrado.
  }
}

/** Chamado no logout: a próxima conta na mesma aba vê o aviso de novo. */
export function esquecerAvisoContexto(): void {
  try {
    sessionStorage.removeItem(CHAVE_AVISO);
  } catch {
    // Sem storage não há o que esquecer.
  }
}
